/** @jsxRuntime classic */
/** @jsx jsx */
import { PropsWithChildren, ReactNode } from 'react';
import { css, jsx } from '@emotion/core';
import { PieCustomLayerProps } from '@nivo/pie';
import { arc } from 'd3-shape';

import { Theme } from 'src/design-system/theme';

type DataType = { id: string; label: ReactNode; color: string; value: number };

type ArcMaskProps = PropsWithChildren<PieCustomLayerProps<DataType>>;
// this component makes a custom layer which helps visually group together (used / remaining) pairs of data.
// data MUST be passed in as pairs for this to component to work
const ArcMask = (props: ArcMaskProps) => {
  const { centerX, centerY, innerRadius, radius, dataWithArc } = props;
  const id = dataWithArc?.[0].id ?? 'fallback';
  const arcGen = arc();

  const layers = dataWithArc.reduce((acc, curr, index) => {
    if (index % 2 === 1) return acc;
    const { startAngle } = curr.arc;
    const { endAngle } = dataWithArc[index + 1].arc;

    acc.push({ startAngle, endAngle });

    return acc;
  }, [] as { startAngle: number; endAngle: number }[]);

  return (
    <svg>
      <defs>
        <mask id={`maskPath-${id}`}>
          {layers.map((data) => {
            return (
              <path
                key={`${data.startAngle}-1`}
                d={
                  arcGen.cornerRadius(0)({
                    innerRadius,
                    outerRadius: radius,
                    startAngle: data.startAngle,
                    endAngle: data.endAngle,
                  }) as any
                }
                css={(theme: Theme) => css`
                  fill: ${theme.colors.light};
                  stroke: ${theme.colors.light};
                `}
                strokeWidth="1"
                style={{ pointerEvents: 'none', transform: `translate(${centerX}px, ${centerY}px)` }}
              />
            );
          })}
          {layers.map((data) => {
            return (
              <path
                key={`${data.startAngle}-2`}
                d={
                  arcGen.cornerRadius(16)({
                    innerRadius,
                    outerRadius: radius,
                    startAngle: data.startAngle,
                    endAngle: data.endAngle,
                  }) as any
                }
                css={(theme: Theme) => css`
                  fill: ${theme.colors.dark};
                `}
                style={{ pointerEvents: 'none', transform: `translate(${centerX}px, ${centerY}px)` }}
              />
            );
          })}
        </mask>
      </defs>
      <g id="my-graphic" mask={`url(#maskPath-${id})`}>
        {layers.map((data) => {
          return (
            <path
              key={`${data.startAngle}-3`}
              d={
                arcGen.cornerRadius(0)({
                  innerRadius,
                  outerRadius: radius,
                  startAngle: data.startAngle,
                  endAngle: data.endAngle,
                }) as any
              }
              fill="white"
              stroke="white"
              strokeWidth="1"
              style={{ pointerEvents: 'none', transform: `translate(${centerX}px, ${centerY}px)` }}
            />
          );
        })}
      </g>
    </svg>
  );
};

export { ArcMask };
